import React from 'react';
import { ACTIVITY_TYPOLOGY, THEME_TYPOLOGY, normalizeMainTheme, parseTypologyTag } from '../data/taxonomies.js';
import { RelatedOrgs } from './chips_related.jsx';

// ============================================================
// Org list item + detail panel
// ============================================================

export function OrgCard({ org, onClick, typeColorMap }) {
  const country = (org.country || '').replace('United Kingdom - ', 'UK · ').replace(';', ' / ');
  const mainThemes = [...new Set(org.themes.map(t => parseTypologyTag(t)?.main).filter(Boolean))].slice(0, 3);
  const mainActs = [...new Set(org.activities.map(t => parseTypologyTag(t)?.main).filter(Boolean))].slice(0, 3);
  const hasProfile = (org.mission || '').trim().length > 20 && (org.activities.length > 0 || org.themes.length > 0);
  return (
    <button className={`org-card ${hasProfile ? 'has-profile' : 'no-profile'}`} onClick={onClick}>
      <div className="org-card-head">
        <div className="org-card-name">{org.name}</div>
        <div className="org-card-country">{country}</div>
      </div>
      {org.mission && <div className="org-card-mission">{org.mission.slice(0, 170)}{org.mission.length > 170 ? '…' : ''}</div>}
      <div className="org-card-tags">
        {mainActs.map(t => {
          const cat = ACTIVITY_TYPOLOGY.find(c => c.name === t);
          return <span key={'a'+t} className="tag tag-act" style={{ background: cat?.color || '#eee' }}>{t}</span>;
        })}
        {mainThemes.map(t => {
          const norm = normalizeMainTheme(t);
          const cat = THEME_TYPOLOGY.find(c => c.name === norm);
          return <span key={'t'+t} className="tag tag-theme" style={{ background: cat?.color || '#eee' }}>{norm}</span>;
        })}
      </div>
      <div className="org-card-meta">
        {org.foundedYear && <span>Est. {org.foundedYear}</span>}
        {org.type && <span>{org.type.split('|')[1]?.trim() || org.type}</span>}
        {org.scale.length > 0 && <span>{org.scale.join(' · ')}</span>}
      </div>
    </button>
  );
}

export function OrgDetail({ org, onClose, allOrgs, onSelectOrg }) {
  if (!org) return null;
  const themes = [...new Set(org.themes.map(parseTypologyTag))].filter(Boolean);
  const activities = [...new Set(org.activities.map(parseTypologyTag))].filter(Boolean);
  const country = (org.country || '').replace('United Kingdom - ', 'UK · ').replace(';', ' / ');
  return (
    <div className="org-detail-overlay" onClick={onClose}>
      <div className="org-detail" onClick={e => e.stopPropagation()}>
        <button className="org-detail-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 20 20" width="18" height="18"><path d="M5 5l10 10M15 5l-10 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
        </button>
        <div className="org-detail-head">
          <div className="org-detail-country">{country}</div>
          <h2 className="org-detail-name">{org.name}</h2>
          {org.website && <a className="org-detail-link" href={org.website.startsWith('http') ? org.website : 'https://' + org.website} target="_blank" rel="noopener">
            {org.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            <svg viewBox="0 0 16 16" width="12" height="12"><path d="M6 3h7v7M13 3L5 11" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round"/></svg>
          </a>}
        </div>

        <div className="org-detail-meta">
          {org.foundedYear && <div><span className="label">Founded</span><span>{org.foundedYear}</span></div>}
          {org.type && <div><span className="label">Type</span><span>{org.type}</span></div>}
          {org.scale.length > 0 && <div><span className="label">Scale</span><span>{org.scale.join(', ')}</span></div>}
        </div>

        {org.mission && <div className="org-detail-section">
          <div className="section-label">Mission</div>
          <p className="mission-text">{org.mission}</p>
        </div>}

        {activities.length > 0 && <div className="org-detail-section">
          <div className="section-label">Activities</div>
          <div className="detail-tags">
            {activities.map((t, i) => {
              const cat = ACTIVITY_TYPOLOGY.find(c => c.name === t.main);
              return <span key={i} className="detail-tag" style={{ background: cat?.color || '#eee' }}>
                <strong>{t.main}</strong>{t.sub ? ` — ${t.sub}` : ''}
              </span>;
            })}
          </div>
        </div>}

        {themes.length > 0 && <div className="org-detail-section">
          <div className="section-label">Themes</div>
          <div className="detail-tags">
            {themes.map((t, i) => {
              const norm = normalizeMainTheme(t.main);
              const cat = THEME_TYPOLOGY.find(c => c.name === norm);
              return <span key={i} className="detail-tag" style={{ background: cat?.color || '#eee' }}>
                <strong>{norm}</strong>{t.sub ? ` — ${t.sub}` : ''}
              </span>;
            })}
          </div>
        </div>}

        {org.stakeholders.length > 0 && <div className="org-detail-section">
          <div className="section-label">Stakeholders engaged</div>
          <div className="stakeholder-list">
            {org.stakeholders.map((s, i) => <span key={i} className="stakeholder">{s}</span>)}
          </div>
        </div>}

        {(org.policyLevels?.length || org.implementationLevels?.length || org.powerLevels?.length) ? <div className="org-detail-section">
          <div className="section-label">Positioning</div>
          <div className="positioning">
            {org.policyLevels?.length > 0 && <MiniSpectrum
              title="Policy" levels={org.policyLevels}
              steps={['Distant', 'Occasional', 'Contributor', 'Partner', 'Actor']}
              from="#FFD6A8" to="#F5A8A8"
            />}
            {org.implementationLevels?.length > 0 && <MiniSpectrum
              title="Implementation" levels={org.implementationLevels}
              steps={['Thinking', 'Indirect', 'Piloting', 'Direct', 'Operator']}
              from="#FBD7E0" to="#D6BFE6"
            />}
            {org.powerLevels?.length > 0 && <MiniSpectrum
              title="Power" levels={org.powerLevels}
              steps={['Building', 'Amplifying', 'Bridging', 'Engaging', 'Within']}
              from="#FFE99A" to="#C9E3A3"
            />}
          </div>
        </div> : null}

        {allOrgs && onSelectOrg && (
          <RelatedOrgs org={org} allOrgs={allOrgs} onSelectOrg={onSelectOrg} n={5}/>
        )}
      </div>
    </div>
  );
}
// Small horizontal spectrum showing 5 steps with the ones the org scores on highlighted.
export function MiniSpectrum({ title, levels, steps, from, to }) {
  // levels are strings like "3 - Policy Contributor" — extract the leading number
  const active = new Set(levels.map(l => parseInt((l.match(/^(\d+)/) || [])[1], 10)).filter(Boolean));
  return (
    <div className="mini-spectrum">
      <div className="mini-spectrum-head">{title}</div>
      <div className="mini-spectrum-track" style={{ '--from': from, '--to': to }}>
        {steps.map((label, i) => {
          const n = i + 1;
          const isActive = active.has(n);
          return (
            <div key={n} className={`mini-spectrum-stop ${isActive ? 'active' : ''}`} style={{ left: `${(i / (steps.length - 1)) * 100}%` }}>
              <div className="mini-spectrum-node">{n}</div>
              <div className="mini-spectrum-label">{label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
